import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import { Quote, Star } from 'lucide-react';
import { FadeUp, ScalePop } from '../../../components/shared/Motion';
import { EditableText, EditableImage } from '../../components';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

function TestimonialCarousel({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  return (
    <section id="reviews" className="relative py-24 lg:py-32 bg-olive-light overflow-hidden">
      <div className="absolute top-10 right-10 w-72 h-72 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(161,201,164,0.4) 1.5px, transparent 1.5px)', backgroundSize: '20px 20px' }} />
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
        </FadeUp>

        <FadeUp delay={0.1}>
          <Swiper
            modules={[Autoplay, Pagination, EffectFade]}
            effect="fade"
            autoplay={{ delay: 5500, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={items.length > 1}
            className="ttss-testimonial-swiper !pb-16"
          >
            {items.map((t, i) => (
              <SwiperSlide key={i}>
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                  <div className="lg:col-span-5">
                    <div className="relative">
                      <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_60px_-25px_rgba(62,38,27,0.4)]">
                        <EditableImage
                          src={t.image?.src}
                          alt={t.image?.alt || t.name}
                          onChange={(src, alt) => updateItem(i, { image: { src, alt } })}
                          className="h-[460px] w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-olive-footer/40 to-transparent" />
                      </div>
                      <ScalePop>
                        <div className="absolute -bottom-4 left-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-ink font-bold text-xs uppercase tracking-[0.18em] shadow-[0_14px_30px_-10px_rgba(197,120,99,0.75)]">
                          <EditableText value={t.name} onChange={(v) => updateItem(i, { name: v })} as="span" />
                        </div>
                      </ScalePop>
                    </div>
                  </div>
                  <div className="lg:col-span-7">
                    <div className="relative bg-white p-8 lg:p-10 rounded-3xl" style={{ border: '2px dashed rgba(161,201,164,0.32)' }}>
                      <Quote className="absolute -top-3 right-6 text-accent" size={64} strokeWidth={1.4} />
                      <EditableText value={t.quote} onChange={(v) => updateItem(i, { quote: v })} as="p" multiline className="text-2xl lg:text-3xl font-display text-ink leading-snug" />
                      <div className="mt-8 flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full overflow-hidden ring-2 ring-accent/40">
                          <EditableImage
                            src={t.image?.src}
                            alt={t.image?.alt || t.name}
                            onChange={(src, alt) => updateItem(i, { image: { src, alt } })}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div>
                          <EditableText value={t.name} onChange={(v) => updateItem(i, { name: v })} as="div" className="font-bold text-olive-deep" />
                          <EditableText value={t.role} onChange={(v) => updateItem(i, { role: v })} as="div" className="text-sm text-olive" />
                        </div>
                        <div className="ml-auto flex gap-1 text-accent">
                          {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={16} fill="currentColor" />)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <style>{`
            .ttss-testimonial-swiper .swiper-pagination-bullet { background:#A1C9A4 !important; opacity:.4 }
            .ttss-testimonial-swiper .swiper-pagination-bullet-active { background:#A1C9A4 !important; opacity:1; width:28px; border-radius:4px; box-shadow:0 0 12px rgba(161,201,164,0.65); }
          `}</style>
        </FadeUp>
      </div>
    </section>
  );
}

export default TestimonialCarousel;
